import React, { useEffect } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';

import { Link } from 'react-router-dom';

import isEmpty from 'lodash/isEmpty';
import groupBy from 'lodash/groupBy';

import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import CloudIcon from '@material-ui/icons/FilterDrama';
import SunIcon from '@material-ui/icons/Brightness5';
import FrostIcon from '@material-ui/icons/AcUnit';

import { RootState } from '../reducers';
import { policyTriggersSelector, fetchPolicyTriggers } from '../reducers/triggers';
import {
  policySelector,
  policiesErrorSelector,
  getSelectedPortfolioPolicies,
} from '../reducers/policies';

import PolicyTriggerCard from '../../components/view/PolicyTriggerCard';
import ErrorPage from '../../components/view/ErrorPage';
import PolicyDetailsSummaryCard from '../../components/view/PolicyDetailsSummaryCard';
import Loader from '../../components/Loader';
import TimeShift from './TimeShift';
import { productByIdSelector } from '../reducers/products';
import { Product } from '../../models';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    padding: theme.spacing(3),
  },
  flex: {
    display: 'flex',
    flexFlow: 'column',
    padding: 0,
  },
  iconItem: {
    display: 'flex',
  },
  iconItemRow: {
    display: 'flex',
    flexGrow: 2,
    justifyContent: 'space-around',
  },
  icon: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: 35,
    margin: '5px 10px',
  },
  alignLeft: {
    textAlign: 'left',
  },
  payment: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  siteMap: {
    textAlign: 'left',
    marginBottom: theme.spacing(1),
  },
  time: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  triggerLabelRoot: {
    display: 'flex',
    textAlign: 'left',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  triggerLabel: {
    display: 'flex',
    flexDirection: 'column',
  },
  triggerIcon: {
    fontSize: 55,
    marginRight: theme.spacing(2),
  },
}));

const mapState = (state: RootState, props: any) => {
  const { policyId } = props.match.params;
  const selectorProps = {
    policy: { id: policyId },
  };
  const products = productByIdSelector(state) || ({} as Product[]);
  const policy = policySelector(state, selectorProps);
  const policyError = policiesErrorSelector(state);
  const portfolioPolicies = getSelectedPortfolioPolicies(state);
  return {
    currentDate: state.time?.currentTime,
    policy,
    policyError,
    portfolioPolicies,
    productCode: products[policy?.product_id!].code,
    policyTriggers: policyTriggersSelector(state, selectorProps),
  };
};

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface PolicyDetailsProps extends PropsFromRedux {
  match: {
    params: {
      policyId: number;
    };
  };
}

function PolicyDetails(props: PolicyDetailsProps) {
  const {
    policy,
    policyError,
    policyTriggers,
    portfolioPolicies,
    productCode,
    currentDate,
  } = props;
  const crop = 'Maize';
  const classes = useStyles();
  const locale = window.sessionStorage.getItem('country') ?? 'en-CA';

  const dispatch = useDispatch();

  useEffect(() => {
    portfolioPolicies!.forEach(pol => {
      dispatch(fetchPolicyTriggers(pol.id, portfolioPolicies!, currentDate));
    });
  }, [currentDate]);

  type TriggerLabelProps = { label: string; icon: any };
  function TriggerLabel({ label, icon: TriggerIcon }: TriggerLabelProps) {
    return (
      <Grid item xs={12}>
        <div className={classes.triggerLabelRoot}>
          <TriggerIcon className={classes.triggerIcon} />
          <div className={classes.triggerLabel}>
            <Typography variant="body2" component="p" color="textSecondary">
              Triggers
            </Typography>
            <Typography variant="h5" component="p">
              {label}
            </Typography>
          </div>
        </div>
      </Grid>
    );
  }

  const triggersByType = groupBy(policyTriggers, 'trigger_type');
  if (policyError != null) {
    return <ErrorPage />;
  } else if (policy && !isEmpty(policy.trigger_location) && policyTriggers) {
    const currencyCode = policy.total_premium.currency;
    return (
      <div>
        <div className={classes.time}>
          <TimeShift
            productType={locale === 'en-US' ? 'almond' : 'maize'}
            policies={portfolioPolicies}
          />
        </div>
        <Typography className={classes.siteMap} variant="body2" color="textSecondary" component="p">
          <Link to="/monitor">Portfolio</Link> {'>'} Policy ID {policy.id}
        </Typography>
        <PolicyDetailsSummaryCard
          productCode={productCode}
          policy={policy}
          crop={crop}
          currentDate={currentDate}
        />
        <Typography className={classes.alignLeft} variant="caption" display="block" gutterBottom>
          Note: The total payout is determined by the maximum payout amount that was triggered by
          the policy.
        </Typography>
        {'fdd' in triggersByType ? (
          <>
            <Grid container spacing={2}>
              <TriggerLabel label="Frost Degree Day" icon={FrostIcon} />
              {triggersByType.fdd.map((trig, idx, arr) => (
                <Grid key={`trg-${idx}`} item xs={12} lg={(12 / arr.length) as any}>
                  <PolicyTriggerCard
                    currentDate={currentDate}
                    trigger={trig}
                    sumInsured={policy.sum_insured!}
                    currencyCode={currencyCode}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
        {'crd' in triggersByType ? (
          <>
            <Grid container spacing={2}>
              <TriggerLabel label="Consecutive Rainy Day" icon={CloudIcon} />
              {triggersByType.crd.map((trig, idx, arr) => (
                <Grid key={`trg-${idx}`} item xs={12} lg={(12 / arr.length) as any}>
                  <PolicyTriggerCard
                    currentDate={currentDate}
                    trigger={trig}
                    sumInsured={policy.sum_insured!}
                    currencyCode={currencyCode}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
        {'crw' in triggersByType ? (
          <>
            <Grid container spacing={2}>
              <TriggerLabel label="5-Day Moving Window Excess Rain" icon={CloudIcon} />
              {triggersByType.crw.map((trig, idx, arr) => (
                <Grid key={`trg-${idx}`} item xs={12} lg={(12 / arr.length) as any}>
                  <PolicyTriggerCard
                    currentDate={currentDate}
                    trigger={trig}
                    sumInsured={policy.sum_insured!}
                    currencyCode={currencyCode}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
        {'crf' in triggersByType ? (
          <>
            <Grid container spacing={2}>
              <TriggerLabel label="Cumulative Rainfall (drought)" icon={SunIcon} />
              {[...(triggersByType.grm || []), ...triggersByType.crf].map((trig, idx, arr) => (
                <Grid key={`trg-${idx}`} item xs={12} lg={(12 / arr.length) as any}>
                  <PolicyTriggerCard
                    currentDate={currentDate}
                    trigger={trig}
                    sumInsured={policy.sum_insured!}
                    currencyCode={currencyCode}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
        {'cdd' in triggersByType ? (
          <>
            <Grid container spacing={2}>
              <TriggerLabel label="Consecutive Dry Day" icon={SunIcon} />
              {triggersByType.cdd.map((trig, idx, arr) => (
                <Grid key={`trg-${idx}`} item xs={12} lg={(12 / arr.length) as any}>
                  <PolicyTriggerCard
                    currentDate={currentDate}
                    trigger={trig}
                    sumInsured={policy.sum_insured!}
                    currencyCode={currencyCode}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              component={Link}
              target="_blank"
              to={`/policies/${policy.id}/terms`}
              color="secondary"
              variant="contained"
              size="small"
            >
              Policy Terms Sheet
            </Button>
          </Grid>
        </Grid>
      </div>
    );
  } else {
    return <Loader />;
  }
}

export default connector(PolicyDetails);
